<template lang="pug">
    .multiplayer-game-menu
        component(
            :is="currentComponentName",
            @commonParametersHasChanged="$emit('commonParametersHasChanged', $event)",
            @playersAmountChanged="$emit('playersAmountChanged', $event)",
            :same-settings-are-active="sameSettingsAreActive",
            :game-state="gameState",
            :slug="slug",
        )
        .multiplayer-game-menu__icons
            svg-icon.check-answers-icon(
                v-if="!answersChecked",
                :key="'check-answers'",
                name="check-answers",
                size="xx-large",
                @mouseover.native="displayCheckAnswersTitle = true",
                @mouseout.native="displayCheckAnswersTitle = false",
                @click.native="checkAnswers",
                :class="{'check-answers-icon_disabled': !checkAnswersReady || isCountdown}"
            )
            svg-icon.continue-game-icon(
                v-else,
                :key="'continue-game'",
                name="reload",
                size="xx-large",
                @mouseover.native="displayContinueGameTitle = true",
                @mouseout.native="displayContinueGameTitle = false",
                @click.native="continueGame",
                :class="{'continue-game-icon_disabled': !continueGameButtonReady}"
            )
            transition(v-if="!answersChecked" name="appear")
                span.check-answers-icon__title.icon-title(v-if="displayCheckAnswersTitle")
                    | {{ $i18n.t('trainer.trainer_multiplayerMenu_button_checkAnswers') }}
            transition(v-else name="appear")
                span.check-answers-icon__title.icon-title(v-if="displayContinueGameTitle")
                    | {{ $i18n.t('trainer.trainer_multiplayerMenu_button_continueGame') }}
            svg-icon.game-status-icon(
                :key="gameState"
                :class="{'game-status-icon_paused': gameState === 'paused'}",
                :name="gameState === 'started' ? 'pause' : 'play'",
                size="xx-large",
                @mouseover.native="displayGameStateTitle = true",
                @mouseout.native="displayGameStateTitle = false",
                @click.native="changeGameState"
            )
            transition(name="appear")
                span.game-status-icon__title.icon-title(v-if="displayGameStateTitle")
                    | {{ gameStateTitle }}
            svg-icon.refresh-game-icon(
                name="refresh-game",
                size="xx-large",
                @mouseover.native="displayRefreshGameTitle = true",
                @mouseout.native="displayRefreshGameTitle = false",
                @click.native="refreshGame",
                :class="{'refresh-game-icon_disabled': gameState !== 'paused'}"
            )
            transition(name="appear")
                span.refresh-game-icon__title.icon-title(v-if="displayRefreshGameTitle")
                    | {{ $i18n.t('trainer.trainer_multiplayerMenu_button_refreshGame') }}
</template>

<script>
    const AdditionAndSubtractionOptions = () => import('../multiplayer-options/AdditionAndSubtraction.vue');
    const MultiplicationAndDivisionOptions = () => import('../multiplayer-options/MultiplicationAndDivision.vue');
    const TabularMultiplicationOptions = () => import ('../multiplayer-options/TabularMultiplication.vue');
    const EquationOptions = () => import('../multiplayer-options/Equation.vue');
    const FactorizationOptions = () => import('../multiplayer-options/Factorization.vue');
    const EquationMultiplicationOptions = () => import('../multiplayer-options/EquationMultiplication.vue');
    const InteractiveFlashCardsOptions = () => import('../multiplayer-options/InteractiveFlashCards.vue');
    const AnzanOptions = () => import('../multiplayer-options/Anzan.vue');
    const RootsAndDegreesOptions = () => import('../multiplayer-options/RootsAndDegrees.vue');
    import MultiplicationAndDivisionMathMagicOptions from '../multiplayer-options/MultiplicationAndDivisionMathMagic';

    const PAUSE_STATE = 'paused';
    const PLAYING_STATE = 'started';

    export default {
        props: {
            slug: {
                type: String,
                default: '',
            },
            sameSettingsAreActive: {
                type: Boolean,
                default: false,
            },
            checkAnswersReady: {
                type: Boolean,
                default: false,
            },
            continueGameButtonReady: {
                type: Boolean,
                default: true,
            },
            playersReadyToAnswer: {
                type: Boolean,
                default: false,
            },
            isCountdown: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                displayCheckAnswersTitle: false,
                displayGameStateTitle: false,
                displayRefreshGameTitle: false,
                displayContinueGameTitle: false,
                answersChecked: false,
                gameState: PAUSE_STATE,
            };
        },
        watch: {
            checkAnswersReady(val) {
                val && (this.answersChecked = false);
            },
            playersReadyToAnswer(val) {
                !val && (this.answersChecked = true);
            },
            isCountdown(val) {
                val && (this.answersChecked = false);
            },
        },
        methods: {
            changeGameState() {
                if (this.gameState === PAUSE_STATE) {
                    this.gameState = PLAYING_STATE;
                    this.answersChecked = false;
                } else {
                    this.gameState = PAUSE_STATE;
                }
                this.$emit('changeGameState', this.gameState);
            },
            refreshGame() {
                if (this.gameState !== PAUSE_STATE) {
                    return;
                }
                this.answersChecked = false;
                this.$emit('refreshGame');
            },
            checkAnswers() {
                this.displayCheckAnswersTitle = false;
                if (!this.checkAnswersReady) {
                    return;
                }
                this.answersChecked = true;
                this.$emit('checkAnswers');
            },
            continueGame() {
                this.displayContinueGameTitle = false;
                if (!this.answersChecked) {
                    return;
                }
                this.answersChecked = false;
                this.$emit('continueGame');
            },
        },
        computed: {
            gameStateTitle() {
                return this.gameState === PAUSE_STATE
                    ? this.$i18n.t('trainer.trainer_multiplayerMenu_button_startGame')
                    : this.$i18n.t('trainer.trainer_multiplayerMenu_button_pauseGame');
            },
            currentComponentName() {
                if (this.slug === 'AdditionAndSubtractionWithTime' || this.slug === 'AdditionAndSubtractionMathMagic') {
                    return 'AdditionAndSubtractionOptions';
                }
                if (this.slug === 'AnzanWithTime') {
                    return 'AnzanOptions';
                }
                if (this.slug === 'RootsAndSquares') {
                    return 'RootsAndDegreesOptions';
                }
                return `${this.slug}Options`;
            },
        },
        components: {
            EquationMultiplicationOptions,
            AdditionAndSubtractionOptions,
            MultiplicationAndDivisionOptions,
            TabularMultiplicationOptions,
            EquationOptions,
            FactorizationOptions,
            InteractiveFlashCardsOptions,
            AnzanOptions,
            MultiplicationAndDivisionMathMagicOptions,
            RootsAndDegreesOptions,
        },
    };
</script>

<style scoped lang="scss">
    .multiplayer-game-menu {
        width: 100%;
        background: $hsl-color-gray-98_4;
        border-radius: $border-radius-20;
        display: flex;
        justify-content: space-between;
        user-select: none;
    }
    .multiplayer-game-menu__icons {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 20px;
        color: $hsl-color-blue-56_5;
        position: relative;
        z-index: 10;
        .svg-icon {
            cursor: pointer;
            margin: 0 5px;
            border: 1px solid $hsl-color-blue-56_5;
            border-radius: 50%;
            &:hover {
                color: $x-color-white;
                background: $hsl-color-blue-56_5;
            }
        }
        .refresh-game-icon {
            margin-right: 0;
        }
        .continue-game-icon {
            border: 0;
            &:hover {
                color: $hsl-color-blue-56_5;
                background: $hsl-color-gray-98_4;
            }
        }
    }

    .icon-title {
        background: $x-color-white;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
        border-radius: 5px;
        color: $x-color-black;
        padding: 10px;
        width: 100%;
        min-width: 200px;
        text-align: center;
        position: absolute;
        top: calc(100% + 5px);
        z-index: 1;
    }

    .check-answers-icon__title {
        right: calc(100% - 40px);
    }

    .game-status-icon_paused {
        color: $x-color-white;
        background: $hsl-color-blue-56_5;
    }

    .game-status-icon__title {
        right: calc(100% - 90px);
    }

    .refresh-game-icon__title {
        right: calc(100% - 140px);
    }

    .refresh-game-icon_disabled,
    .check-answers-icon_disabled,
    .continue-game-icon_disabled {
        opacity: 0.4;
        user-select: none;
        pointer-events: none;
    }

    .appear-enter-active,
    .appear-leave-active {
        opacity: 1;
        transition: all 0.3s;
    }
    .appear-enter,
    .appear-leave-to {
        opacity: 0;
    }
</style>
